import React from "react";
import { BookOpen, ClipboardCheck, ShieldCheck, Workflow } from "lucide-react";

const OLLIE_WELCOME_IMAGE = `${process.env.PUBLIC_URL}/assets/images/icons/ollie-claim-welcome.png`;

const CLAIM_BENEFITS = [
	{
		icon: ClipboardCheck,
		title: "A helping hand for your team",
		detail: "Use Ollie internally to assist your team with reviewing plans and drafting comment letters."
	},
	{
		icon: BookOpen,
		title: "Your adopted code and submittal requirements",
		detail: "Manage Ollie's review criteria to give applicants clear guidance before they formally submit."
	},
	{
		icon: Workflow,
		title: "Designed to fit your workflow",
		detail: "Streamline your existing review process without adding complexity. No integration required."
	}
];

type ClaimJurisdictionLayoutProps = {
	title: React.ReactNode;
	// The card on the right: the claim form or the setup checklist.
	children: React.ReactNode;
};

/**
 * Page frame of the /claim and /claim/setup screens: the audience pill, Ollie, the introduction
 * with the benefits on the left, and the card on the right. On screens narrower than 1024px the
 * card goes under the introduction.
 */
const ClaimJurisdictionLayout = ({ title, children }: ClaimJurisdictionLayoutProps) => {
	return (
		<div className="claim-jurisdiction-layout">
			<div className="claim-jurisdiction-layout-content">
				<div className="claim-jurisdiction-layout-header">
					<div className="claim-jurisdiction-layout-audience">
						<ShieldCheck size={16} className="claim-jurisdiction-layout-audience-icon" />
						For fire marshals &amp; AHJ staff
					</div>
				</div>

				<div className="claim-jurisdiction-layout-grid">
					<img
						src={OLLIE_WELCOME_IMAGE}
						alt="Ollie waving hello in an orange safety helmet"
						className="claim-jurisdiction-layout-ollie"
					/>

					<div className="claim-jurisdiction-layout-intro">
						<div className="claim-jurisdiction-layout-eyebrow">Welcome in! let&apos;s get started!</div>
						<h1 className="claim-jurisdiction-layout-title">{title}</h1>
						<p className="claim-jurisdiction-layout-description">
							Claim your jurisdiction to reduce plan review cycles and give your community a faster path to compliance.
						</p>

						<div className="claim-jurisdiction-layout-benefits">
							{CLAIM_BENEFITS.map(({ icon: BenefitIcon, title: benefitTitle, detail }) => (
								<div key={benefitTitle} className="claim-jurisdiction-layout-benefit">
									<div className="claim-jurisdiction-layout-benefit-icon">
										<BenefitIcon size={16} />
									</div>
									<div className="claim-jurisdiction-layout-benefit-text">
										<div className="claim-jurisdiction-layout-benefit-title">{benefitTitle}</div>
										<div className="claim-jurisdiction-layout-benefit-detail">{detail}</div>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="claim-jurisdiction-layout-card">{children}</div>
				</div>
			</div>
		</div>
	);
};

export default ClaimJurisdictionLayout;
